import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  Inject,
  inject,
  OnInit,
  signal
} from '@angular/core';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {SessionStoreDataService} from '../../projects/products-lib/src/lib/services/session-store-data.service';
import {HeaderComponent} from '../components/header/header-component';
import {FooterComponent} from '../components/footer-component/footer-component';
import {AuthService, SessionExpireService, SSO_WAITING_PATH} from 'auth';
import {AuthValidationService} from '../../projects/auth/src/lib/services/auth-validation.service';
import {filter, take} from 'rxjs';
import {ModalConfig} from '../../projects/products-lib/src/lib/models/modal-config.model';
import {
  ModalComponent
} from '../../projects/products-lib/src/lib/components/modal-popup-component/modal-popup-component';
import {HeaderParamsService} from '../services/header-params.service';
import {
  GoogleAnalyticsEventService
} from '../../projects/products-lib/src/lib/services/google-analytics/google-analytics-event.service';
import {CookieService} from '../services/cookie.service';
import {COOKIE_CONSENT_COOKIE_NAME} from '../services/cookie-constants';
import {CookieConsentService} from '../services/cookie-consent.service';
import {Title} from '@angular/platform-browser';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import {routeRequiresAuth} from '../services/route-requires-auth';

@Component({
  imports: [RouterModule, HeaderComponent, FooterComponent, ModalComponent],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit, AfterViewInit {
  protected title = 'CGP RevisionHub';
  readonly cookieBannerVisible = signal(false);
  readonly currentRoute = signal('');
  readonly cookieConsentCookieName = COOKIE_CONSENT_COOKIE_NAME;
  modalConfig: ModalConfig | null = null;
  showModal = false;

  /** True while the bootstrap refresh is in flight — suppresses the session-lost redirect */
  private readonly authBootstrapping = signal(true);

  private readonly sessionStoreDataService = inject(SessionStoreDataService);
  private readonly authValidationService = inject(AuthValidationService);
  private readonly cookieConsentService = inject(CookieConsentService);

  /** Public routes that should never trigger an auth redirect */
  private readonly publicRoutes = ['/login', '/', '/product-selector', '/11-plus-online-tests', '/11-plus-online-account', SSO_WAITING_PATH];

  private readonly elevenPlusRoutes = [ '/11-plus-online-tests', '/11-plus-online-account'];

  constructor(
    @Inject(ENV_TOKEN)
    protected readonly environment: EnvInterface,
    private authService: AuthService,
    private readonly sessionExpireService: SessionExpireService,
    private el: ElementRef,
    private router: Router,
    private headerParamsService: HeaderParamsService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly gaService: GoogleAnalyticsEventService,
    private readonly cookieService: CookieService,
    private titleService: Title,
  ) {
    effect(() => {
      const isLoggedIn = this.sessionStoreDataService.isLoggedInSignal();
      const bootstrapping = this.authBootstrapping();
      const currentUrl = this.router.url;
      const isPublic = this.publicRoutes.some(r => currentUrl === r || currentUrl.startsWith(r + '?'));

      // Don't redirect while bootstrap refresh is still in progress
      if (bootstrapping) return;

      // Only routes behind authGuard need a session. Without this check, a logged-out
      // refresh of /samples/... was sent to /login whenever the cached lazy chunk let
      // navigation finish before the bootstrap refresh call returned.
      if (!isLoggedIn && !isPublic && routeRequiresAuth(this.router)) {
        console.log('* App effect: session lost, redirecting to /login');
        this.router.navigate(['/login'], {queryParams: {returnUrl: currentUrl}});
      }
    });

    // Hide cookie banner when user saves any preference
    effect(() => {
      // Reading the signal triggers re-evaluation when preferences change
      this.cookieConsentService.preferences();
      if (this.hasCookieConsent()) {
        this.cookieBannerVisible.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.authService.checkSharedLogin();
    // Set header info immediately on init (signals don't need DOM)
    this.handleRouteChange(window.location.pathname);

    this.titleService.setTitle(`${this.environment.app.title}`);

    // If no access token but refresh cookie may exist, silently refresh before
    // the session-lost effect can fire and redirect to login.
    // Skip if on /login, if the user explicitly logged out, or during an inbound SSO handoff
    // (it is establishing the session, and a concurrent refresh would rotate the cookie under it).
    const currentPath = window.location.pathname;
    const wasExplicitLogout = localStorage.getItem('explicitLogout') === 'true';

    if (!this.authValidationService.getStoredToken() && currentPath !== '/login' && currentPath !== SSO_WAITING_PATH && !wasExplicitLogout) {
      //console.log('App: no access token on bootstrap, attempting silent refresh via cookie...');
      const intendedUrl = window.location.pathname + window.location.search;
      this.authValidationService.validateToken().subscribe(result => {
        //console.log('App: bootstrap token validation result:', result.isValid);
        this.authBootstrapping.set(false);
        if (result.isValid) {
          // Bump the session signal so the UI reflects the logged-in state
          this.sessionStoreDataService.notifyTokenChange();
          if (intendedUrl && intendedUrl !== '/' && intendedUrl !== '/login') {
            //console.log('App: bootstrap refresh succeeded, navigating to', intendedUrl);
            // Use Angular router instead of full reload — a reload would consume
            // the (already-rotated) refresh cookie a second time and get a 401.
            this.router.navigateByUrl(intendedUrl);
          }
        }
      });
    } else {
      this.authBootstrapping.set(false);
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute.set(event.urlAfterRedirects);
        this.handleRouteChange(event.urlAfterRedirects);
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // If logged in but has no active products at all, redirect to product-selector (skip for pupil members)
        const url = event.urlAfterRedirects;
        const isPublicRoute = this.publicRoutes.some(r => url === r || url.startsWith(r + '?'));
        const productSelected = !!sessionStorage.getItem('productSelected');
        const decodedToken = this.sessionStoreDataService.getDecodedTokenUser();
        const hasNoProducts = !decodedToken?.hasActiveSubscription && !decodedToken?.hasActiveDigitalProducts;
        if (this.sessionStoreDataService.isLoggedInSignal() && hasNoProducts && !isPublicRoute && url !== '/product-selector' && !productSelected && !decodedToken?.isPupilMember) {
          this.router.navigate(['/product-selector']);
        }
      }
    });

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      take(1)
    ).subscribe(() => {
      this.el.nativeElement.classList.add('app-ready');
    });

    this.cookieBannerVisible.set(!this.hasCookieConsent());
  }


  onConfirm(): void {
    //console.log('Modal confirmed');
    this.modalConfig = null;
    this.showModal = false;
    this.cdRef.detectChanges();
  }

  onReject(): void {
    //console.log('Modal rejected');
    this.modalConfig = null;
    this.showModal = false;
    this.cdRef.detectChanges();
  }

  onModalClose(): void {
    this.modalConfig = null;
    this.showModal = false;
    this.cdRef.detectChanges();
  }

  onUserActivity() {
    //TODO temp removal to stop inactive logout
    // fromEvent<MouseEvent>(document, 'mousemove')
    //   .pipe(debounceTime(15000))
    //   .subscribe(() => {
    //     // Reset the session timer on user activity
    //     this.sessionExpireService.resetSessionTimer();
    //   });
  }

  ngAfterViewInit(): void {
    this.gaService.setUpAnalytics();
    this.onUserActivity();
    // Set header info on initial load (DOM is now ready)
    this.handleRouteChange(this.router.url);
  }

  isElevenPlusRoute(url: string): boolean {
    return this.elevenPlusRoutes.findIndex((epr) => url.startsWith(epr)) >= 0;
  }


  handleRouteChange(url: string) {
    let productCode = 'default';
    if (this.isElevenPlusRoute(url)) {
      productCode = '11-plus-online-tests';
    } else if (url.includes('/products') || url.includes('/samples')) {
      const productCodeMatch = url.match(/products\/(\w+)/) || url.match(/samples\/(\w+)/);
      productCode = productCodeMatch ? productCodeMatch[1].toLowerCase() : '';
    }
    this.headerParamsService.setHeaderInfo(productCode);
  }

  acceptCookies(): void {
    this.cookieConsentService.savePreferences({advertising: true, analytics: true});
    this.cookieBannerVisible.set(false);
  }

  dismissCookieBanner(): void {
    this.cookieConsentService.savePreferences({advertising: false, analytics: false});
    this.cookieBannerVisible.set(false);
  }

  private hasCookieConsent(): boolean {
    const consent = this.cookieService.get<{ accepted?: boolean }>(this.cookieConsentCookieName);
    return consent != null;
  }

  protected gotToCookiePage() {
    this.router.navigateByUrl('/cookies');
  }
}
